/**
 * 4 em Linha — as regras, e só as regras.
 *
 * Vive no pacote partilhado e não no componente por uma razão que decide o
 * desenho todo: o jogo NÃO tem servidor. O estado viaja pelo canal de dados da
 * chamada (o mesmo SFU que leva a voz, ver `Whiteboard`), e o que cada cliente
 * recebe é a JOGADA — uma coluna — não o tabuleiro. Cada um aplica as mesmas
 * regras à mesma sequência e chega ao mesmo tabuleiro; é a pureza destas
 * funções que garante que dois ecrãs mostram a mesma coisa. Uma regra impura
 * (relógio, aleatório, mutação em sítio) partia a convergência sem um erro.
 *
 * Daí três propriedades que os testes fecham:
 *  1. PURAS — mesma entrada, mesma saída, sempre. Nada de `Date`/`Math.random`.
 *  2. IMUTÁVEIS — `drop` devolve um estado NOVO. Mutar em sítio faria o React
 *     perder a mudança e faria um `sync` reenviado corromper o histórico.
 *  3. SERIALIZÁVEIS — só números e arrays de números. É isto que entra no
 *     `JSON.stringify` do pacote de sincronização, sem classes nem `Map`.
 */
export const C4_COLS = 7;
export const C4_ROWS = 6;
export const C4_CELLS = C4_COLS * C4_ROWS;
/** Quantas peças seguidas ganham. O nome do jogo, mas escrito uma vez. */
export const C4_LINE = 4;
/** Índice da casa a partir da coluna e da linha. Linha 0 é o TOPO do tabuleiro. */
export function c4Index(col, row) {
    return row * C4_COLS + col;
}
/** Tabuleiro vazio, com o jogador 1 a abrir. */
export function c4Empty() {
    return { cells: new Array(C4_CELLS).fill(0), turn: 1, outcome: 0, line: [], moves: 0 };
}
/**
 * Em que linha assenta uma peça largada nesta coluna, ou -1 se a coluna está
 * cheia. Procura-se de BAIXO para cima porque é isso que a gravidade faz — e é
 * também o que torna `c4Drop` O(6) em vez de contar peças.
 */
export function c4LandingRow(state, col) {
    if (col < 0 || col >= C4_COLS)
        return -1;
    for (let row = C4_ROWS - 1; row >= 0; row -= 1) {
        if (state.cells[c4Index(col, row)] === 0)
            return row;
    }
    return -1;
}
/** Pode-se largar aqui? Coluna válida, com espaço, e o jogo por acabar. */
export function c4CanDrop(state, col) {
    return state.outcome === 0 && c4LandingRow(state, col) >= 0;
}
/** As quatro direcções a verificar. A oposta de cada uma é a mesma reta, por
 *  isso quatro chegam: horizontal, vertical e as duas diagonais. */
const DIRS = [
    [1, 0],
    [0, 1],
    [1, 1],
    [1, -1],
];
/**
 * A linha de 4 (ou mais) que passa pela peça em (col,row), ou null.
 *
 * Verifica-se a partir da ÚLTIMA peça e não o tabuleiro inteiro: uma vitória só
 * pode incluir a peça acabada de cair, logo varrer as 69 retas possíveis a cada
 * jogada seria trabalho a mais para a mesma resposta. Devolve os índices para
 * quem desenha poder acender exactamente as casas que ganharam.
 */
export function c4LineThrough(cells, col, row) {
    const who = cells[c4Index(col, row)];
    if (!who)
        return null;
    for (const [dc, dr] of DIRS) {
        const run = [c4Index(col, row)];
        // Estica para os dois lados da direcção até a cor mudar ou a peça sair do
        // tabuleiro. O `sign` de -1 é a metade oposta da mesma reta.
        for (const sign of [1, -1]) {
            let c = col + dc * sign;
            let r = row + dr * sign;
            while (c >= 0 && c < C4_COLS && r >= 0 && r < C4_ROWS && cells[c4Index(c, r)] === who) {
                run.push(c4Index(c, r));
                c += dc * sign;
                r += dr * sign;
            }
        }
        if (run.length >= C4_LINE)
            return run.sort((a, b) => a - b);
    }
    return null;
}
/**
 * Larga uma peça do jogador da vez na coluna, e devolve o estado seguinte.
 *
 * Devolve o MESMO objecto quando a jogada é ilegal (coluna cheia, fora do
 * tabuleiro, jogo acabado). Isso não é preguiça: é o que torna a rede segura.
 * Um pacote repetido ou fora de ordem aplica-se a um estado onde já não é
 * legal, não muda nada, e os dois ecrãs continuam iguais — sem uma única
 * verificação de duplicados no transporte.
 */
export function c4Drop(state, col) {
    const row = state.outcome === 0 ? c4LandingRow(state, col) : -1;
    if (row < 0)
        return state;
    const cells = state.cells.slice();
    cells[c4Index(col, row)] = state.turn;
    const line = c4LineThrough(cells, col, row);
    const moves = state.moves + 1;
    const outcome = line ? state.turn : moves >= C4_CELLS ? 3 : 0;
    return {
        cells,
        turn: state.turn === 1 ? 2 : 1,
        outcome,
        line: line ?? [],
        moves,
    };
}
/**
 * Reconstrói o tabuleiro a partir da lista de colunas jogadas.
 *
 * É isto que faz o catch-up de quem entra a meio: em vez de confiar num estado
 * recebido (que pode vir corrompido de um cliente desactualizado, e é território
 * partilhado), reaplica-se a HISTÓRIA às regras locais. Um histórico com uma
 * jogada inválida pára aí em vez de rebentar — o pior caso é um tabuleiro mais
 * curto, nunca um tabuleiro impossível.
 */
export function c4Replay(moves) {
    let state = c4Empty();
    for (const col of moves) {
        const next = c4Drop(state, col);
        if (next === state)
            break; // jogada impossível: o histórico acaba aqui
        state = next;
    }
    return state;
}
/** As colunas onde ainda se pode jogar. Usado para desligar os botões cheios. */
export function c4OpenColumns(state) {
    const out = [];
    for (let col = 0; col < C4_COLS; col += 1)
        if (c4CanDrop(state, col))
            out.push(col);
    return out;
}
//# sourceMappingURL=connect4.js.map